import { AdmPlan, SubjectTypeProps, UserId } from "@custom-types/base-types";
import { createSelector } from "@reduxjs/toolkit";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { plansAdapter } from "@store/plans/plans-slice";
import { RootState } from "@store/store-helper";
import {
  doesCopyOverlapCopiedPlan,
  getAttachedSceneSeats,
  getSceneSeatAssignees,
} from "@utils/plan-copy-utils";
import { BaseProduct, ProductType } from "@utils/product/product-utils-types";
import { is2026SphereXgProductIdentifier } from "@utils/type-guards";
import {
  effectiveDashboardApp,
  offersPlanAppChoice,
  planAppFeatures,
  PlanAppSettings,
} from "@utils/product/plan-apps";
import { isActiveUsersLimitType } from "@utils/limit-utils";

/** Get all the products of provided type from the store for the provided subjectType */
export function allProductsOfTypeSelector<T extends ProductType>({
  productType,
  subjectType,
}: {
  productType: T;
} & SubjectTypeProps): (state: RootState) => BaseProduct<T>[] {
  return createSelector(
    (state: RootState) => state,
    (state: RootState) => {
      const shouldShowAllProducts = state.devFeatures.shouldShowAllProducts;
      const isBetaTestingEnabled = state.app.isBetaTestingEnabled;
      const allProductsOfType = state.planCreation.products[productType];

      if (!allProductsOfType) {
        return [];
      }

      /** All the products of type productType that have subjectType as their scope */
      return Object.values(allProductsOfType).filter((product) => {
        const typedProduct = product as BaseProduct<T>;
        return (
          typedProduct.scope[subjectType].shouldShow &&
          (typedProduct.isCoreProduct ||
            shouldShowAllProducts ||
            (is2026SphereXgProductIdentifier(typedProduct.identifier) &&
              isBetaTestingEnabled))
        );
      }) as BaseProduct<T>[];
    }
  );
}

/**
 * Number of SCENE seats that come with the plan that is being created.
 *
 * Products that include SCENE seats get one seat per active user the plan allows,
 * so the count always mirrors the "Active Users" limit. It is 0 for every other product.
 */
export const sceneSeatsToCreateSelector = createSelector(
  (state: RootState) => state.planCreation.selectedProduct,
  (state: RootState) => state.planCreation.allLimits,
  (selectedProduct, allLimits): number => {
    if (!selectedProduct?.hasSceneSeats) {
      return 0;
    }

    const activeUsersLimit = allLimits.find(
      (limit) => isActiveUsersLimitType(limit.type) && limit.isEnabled
    );

    return activeUsersLimit?.value ?? 0;
  }
);

/** Get limits that are enabled, dropping the frontend-only flags so they never reach the API */
export const activePlanLimitsSelector = createSelector(
  (state: RootState) => state.planCreation.allLimits,
  (allLimits): FaroCoreApiTypes.LimitPayload[] =>
    allLimits.reduce<FaroCoreApiTypes.LimitPayload[]>((acc, limit) => {
      if (limit.isEnabled) {
        acc.push({ type: limit.type, value: limit.value, roles: limit.roles });
      }

      return acc;
    }, [])
);

/**
 * Whether the plan that is being created can carry the application settings. They belong to the
 * workspace subscription, so they are only offered for workspace plans of a product that has a
 * choice of applications to make.
 */
export const shouldConfigurePlanAppsSelector = createSelector(
  (state: RootState) => state.planCreation.selectedProduct,
  (state: RootState) => state.planCreation.planSubject.planSubjectType,
  (selectedProduct, planSubjectType): boolean =>
    planSubjectType === "COMPANY" &&
    selectedProduct !== null &&
    offersPlanAppChoice(selectedProduct)
);

/** The applications the plan opens, null for plans that do not carry them */
export const planAppSettingsSelector = createSelector(
  shouldConfigurePlanAppsSelector,
  (state: RootState) => state.planCreation.appSettings,
  (shouldConfigurePlanApps, appSettings): PlanAppSettings | null =>
    shouldConfigurePlanApps
      ? {
          defaultViewerApp: appSettings.defaultViewerApp,
          defaultDashboardApp: effectiveDashboardApp(appSettings),
        }
      : null
);

/** The features that attach the chosen application settings to the created subscription */
export const planAppFeaturesSelector = createSelector(
  shouldConfigurePlanAppsSelector,
  (state: RootState) => state.planCreation.appSettings,
  (
    shouldConfigurePlanApps,
    appSettings
  ): FaroCoreApiTypes.FeatureIdentifier[] =>
    shouldConfigurePlanApps ? planAppFeatures(appSettings) : []
);

/** The SCENE seats of the plan that is copied, or none when a plan is created from scratch */
export const copiedPlanSceneSeatsSelector = createSelector(
  (state: RootState) => state.planCreation.copiedPlan,
  (state: RootState) => state.plans,
  (copiedPlan, plansState): AdmPlan[] =>
    copiedPlan
      ? getAttachedSceneSeats(
          plansAdapter.getSelectors().selectAll(plansState),
          copiedPlan.plan.id
        )
      : []
);

/** The members holding a SCENE seat of the plan that is copied, who get a seat of the copy */
export const copiedPlanSceneSeatAssigneesSelector = createSelector(
  copiedPlanSceneSeatsSelector,
  (sceneSeats): UserId[] => getSceneSeatAssignees(sceneSeats)
);

/**
 * Whether the copy would run at the same time as the plan it is copied from. Only then is the
 * user offered to end the copied plan when the copy starts.
 */
export const doesCopyOverlapCopiedPlanSelector = createSelector(
  (state: RootState) => state.planCreation.copiedPlan,
  (state: RootState) => state.planCreation.creationDates.startDate,
  (state: RootState) => state.planCreation.creationDates.endDate,
  (copiedPlan, startDate, endDate): boolean =>
    copiedPlan !== null &&
    doesCopyOverlapCopiedPlan({
      copiedPlan: copiedPlan.plan,
      copyDates: { startDate, endDate },
    })
);

/**
 * Whether the copied plan ends when the copy starts: the user has to have chosen so, which is only
 * offered while both plans overlap. A copy that starts after the copied plan leaves it unchanged.
 */
export const isEndingCopiedPlanSelector = createSelector(
  (state: RootState) => state.planCreation.copiedPlan,
  doesCopyOverlapCopiedPlanSelector,
  (copiedPlan, isOverlapping): boolean =>
    isOverlapping && copiedPlan?.shouldEndCopiedPlan === true
);
